import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchEverything, useWorkspaces, type SearchItem } from '../lib/workspaces.js';
import Icon, { type IconName } from '../ui/Icon.js';

/** Icon + group heading per result kind, in the order groups are shown. */
const KINDS: { kind: string; label: string; icon: IconName; tint: string }[] = [
  { kind: 'workspace', label: 'Workspaces', icon: 'grid', tint: '#5e5ce6' },
  { kind: 'project', label: 'Projects', icon: 'folder', tint: '#5e5ce6' },
  { kind: 'task', label: 'Action items', icon: 'tasks', tint: '#0a7c72' },
  { kind: 'person', label: 'People', icon: 'users', tint: '#0a84ff' },
  { kind: 'message', label: 'Messages', icon: 'mail', tint: '#0a84ff' },
  { kind: 'event', label: 'Meetings & events', icon: 'calendar', tint: '#ff9f0a' },
  { kind: 'note', label: 'Notes', icon: 'bulb', tint: '#ffb800' },
  { kind: 'file', label: 'Files', icon: 'file', tint: '#8e8e93' },
];
const OTHER = { label: 'Other', icon: 'circle' as IconName, tint: '#8e8e93' };

export interface PaletteGroup {
  kind: string;
  label: string;
  icon: IconName;
  tint: string;
  items: SearchItem[];
}

/** Results grouped by kind (known kinds in a fixed order, unknown ones last), server rank kept within a group. */
export function groupResults(items: readonly SearchItem[]): PaletteGroup[] {
  const groups: PaletteGroup[] = [];
  for (const k of KINDS) {
    const mine = items.filter((i) => i.kind === k.kind);
    if (mine.length) groups.push({ ...k, items: mine });
  }
  const rest = items.filter((i) => !KINDS.some((k) => k.kind === i.kind));
  if (rest.length) groups.push({ kind: 'other', ...OTHER, items: rest });
  return groups;
}

const DEBOUNCE_MS = 150;

/**
 * ⌘K palette (spec 2026-10-14): a centred search box over everything. Typing
 * searches (debounced 150ms); ↑/↓ move, Enter opens, Esc closes. With nothing
 * typed it offers quick links: Today, Action items and each workspace.
 */
export default function SearchPalette({ onClose }: { onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const workspaces = useWorkspaces();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Focus in, restore focus and page scroll on close.
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    input.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, []);

  const term = q.trim();
  useEffect(() => {
    if (term.length === 0) {
      setResults(null);
      setError(null);
      setLoading(false);
      return;
    }
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setLoading(true);
    const t = setTimeout(() => {
      searchEverything(term, ctl?.signal).then(
        (items) => {
          if (ctl?.signal.aborted) return;
          setResults(items);
          setError(null);
          setLoading(false);
          setCursor(0);
        },
        (err: unknown) => {
          if (ctl?.signal.aborted) return;
          setError(err instanceof Error ? err.message : 'Search failed');
          setLoading(false);
        },
      );
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      ctl?.abort();
    };
  }, [term]);

  const quick: PaletteGroup[] = useMemo(
    () => [
      {
        kind: 'quick',
        label: 'Go to',
        icon: 'chevron-right',
        tint: '#0a7c72',
        items: [
          { kind: 'page', id: 'today', title: 'Today', sub: null, url: '/' },
          { kind: 'page', id: 'tasks', title: 'Action items', sub: null, url: '/tasks' },
          { kind: 'page', id: 'inbox', title: 'Inbox', sub: null, url: '/inbox' },
          { kind: 'page', id: 'projects', title: 'All projects', sub: null, url: '/projects' },
          ...workspaces.map((w) => ({
            kind: 'workspace',
            id: w.key,
            title: w.name,
            sub: w.counts?.openItems ? `${w.counts.openItems} open items` : 'Workspace',
            url: `/w/${encodeURIComponent(w.key)}`,
          })),
        ],
      },
    ],
    [workspaces],
  );

  const groups = term.length === 0 ? quick : groupResults(results ?? []);
  const flat = groups.flatMap((g) => g.items.map((item) => ({ item, group: g })));
  const safeCursor = flat.length ? Math.min(cursor, flat.length - 1) : -1;
  const optId = (i: number): string => `sp-opt-${i}`;

  // Keep the highlighted row in view.
  useEffect(() => {
    if (safeCursor < 0) return;
    const el = list.current?.querySelector<HTMLElement>(`#${optId(safeCursor)}`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [safeCursor]);

  const go = (item: SearchItem): void => {
    closeRef.current();
    navigate(item.url);
  };

  const onKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeRef.current();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (flat.length) setCursor((c) => (Math.min(c, flat.length - 1) + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (flat.length) setCursor((c) => (Math.min(c, flat.length - 1) - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[safeCursor];
      if (hit) go(hit.item);
    }
  };

  let n = -1;
  return (
    <div className="sp-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeRef.current()}>
      <div className="sp" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKey}>
        <div className="sp-field">
          <Icon name="search" size={20} className="sp-field__icon" />
          <input
            ref={input}
            className="sp-input"
            type="text"
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls="sp-list"
            aria-autocomplete="list"
            aria-activedescendant={safeCursor >= 0 ? optId(safeCursor) : undefined}
            aria-label="Search everything"
            placeholder="Search projects, people, items, notes, messages…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <button type="button" className="sp-esc" onClick={() => closeRef.current()} aria-label="Close search">
            Esc
          </button>
        </div>
        <div className="sp-list" id="sp-list" role="listbox" aria-label="Results" ref={list}>
          {groups.map((g) => (
            <div key={g.kind} role="group" aria-label={g.label} className="sp-group">
              <div className="sp-group__title" aria-hidden="true">
                {g.label}
              </div>
              {g.items.map((item) => {
                n += 1;
                const i = n;
                const kindMeta = KINDS.find((k) => k.kind === item.kind);
                const icon = kindMeta?.icon ?? (item.kind === 'page' ? 'chevron-right' : g.icon);
                const tint = kindMeta?.tint ?? g.tint;
                const on = i === safeCursor;
                return (
                  <div
                    key={`${item.kind}:${item.id}:${item.url}`}
                    id={optId(i)}
                    role="option"
                    aria-selected={on}
                    className={`sp-opt${on ? ' sp-opt--on' : ''}`}
                    onMouseMove={() => cursor !== i && setCursor(i)}
                    onClick={() => go(item)}
                  >
                    <span className="sp-opt__icon" style={{ '--tint': tint } as CSSProperties} aria-hidden="true">
                      <Icon name={icon} size={16} />
                    </span>
                    <span className="sp-opt__text">
                      <span className="sp-opt__title">{item.title}</span>
                      {item.sub && <span className="sp-opt__sub">{item.sub}</span>}
                    </span>
                    {on && <span className="sp-opt__enter" aria-hidden="true">↵</span>}
                  </div>
                );
              })}
            </div>
          ))}
          {term.length > 0 && !loading && !error && results && results.length === 0 && (
            <p className="sp-empty">Nothing matches “{term}”.</p>
          )}
          {term.length > 0 && error && <p className="sp-empty">Search failed: {error}</p>}
          {term.length > 0 && loading && !results && <p className="sp-empty">Searching…</p>}
        </div>
        <div className="sp-foot" aria-hidden="true">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span>
            <kbd>Enter</kbd> open
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
